import { Injectable, Inject, Optional } from '@angular/core';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { DateTime, Info, Settings } from 'luxon';

// Month translations (same as in datepicker.component.ts)
const monthTranslations = {
  'January': {
    en: 'January',
    es: 'Enero',
    ar: 'يناير',
    fr: 'Janvier',
    pt: 'Janeiro',
    de: 'Januar',
    ja: '1月',
    it: 'Gennaio'
  },
  'February': {
    en: 'February',
    es: 'Febrero',
    ar: 'فبراير',
    fr: 'Février',
    pt: 'Fevereiro',
    de: 'Februar',
    ja: '2月',
    it: 'Febbraio'
  },
  'March': {
    en: 'March',
    es: 'Marzo',
    ar: 'مارس',
    fr: 'Mars',
    pt: 'Março',
    de: 'März',
    ja: '3月',
    it: 'Marzo'
  },
  'April': {
    en: 'April',
    es: 'Abril',
    ar: 'أبريل',
    fr: 'Avril',
    pt: 'Abril',
    de: 'April',
    ja: '4月',
    it: 'Aprile'
  },
  'May': {
    en: 'May',
    es: 'Mayo',
    ar: 'مايو',
    fr: 'Mai',
    pt: 'Maio',
    de: 'Mai',
    ja: '5月',
    it: 'Maggio'
  },
  'June': {
    en: 'June',
    es: 'Junio',
    ar: 'يونيو',
    fr: 'Juin',
    pt: 'Junho',
    de: 'Juni',
    ja: '6月',
    it: 'Giugno'
  },
  'July': {
    en: 'July',
    es: 'Julio',
    ar: 'يوليو',
    fr: 'Juillet',
    pt: 'Julho',
    de: 'Juli',
    ja: '7月',
    it: 'Luglio'
  },
  'August': {
    en: 'August',
    es: 'Agosto',
    ar: 'أغسطس',
    fr: 'Août',
    pt: 'Agosto',
    de: 'August',
    ja: '8月',
    it: 'Agosto'
  },
  'September': {
    en: 'September',
    es: 'Septiembre',
    ar: 'سبتمبر',
    fr: 'Septembre',
    pt: 'Setembro',
    de: 'September',
    ja: '9月',
    it: 'Settembre'
  },
  'October': {
    en: 'October',
    es: 'Octubre',
    ar: 'أكتوبر',
    fr: 'Octobre',
    pt: 'Outubro',
    de: 'Oktober',
    ja: '10月',
    it: 'Ottobre'
  },
  'November': {
    en: 'November',
    es: 'Noviembre',
    ar: 'نوفمبر',
    fr: 'Novembre',
    pt: 'Novembro',
    de: 'November',
    ja: '11月',
    it: 'Novembre'
  },
  'December': {
    en: 'December',
    es: 'Diciembre',
    ar: 'ديسمبر',
    fr: 'Décembre',
    pt: 'Dezembro',
    de: 'Dezember',
    ja: '12月',
    it: 'Dicembre'
  },
  // Short month names
  'Jan': {
    en: 'Jan',
    es: 'Ene',
    ar: 'ينا',
    fr: 'Jan',
    pt: 'Jan',
    de: 'Jan',
    ja: '1月',
    it: 'Gen'
  },
  'Feb': {
    en: 'Feb',
    es: 'Feb',
    ar: 'فبر',
    fr: 'Fév',
    pt: 'Fev',
    de: 'Feb',
    ja: '2月',
    it: 'Feb'
  },
  'Mar': {
    en: 'Mar',
    es: 'Mar',
    ar: 'مار',
    fr: 'Mar',
    pt: 'Mar',
    de: 'Mär',
    ja: '3月',
    it: 'Mar'
  },
  'Apr': {
    en: 'Apr',
    es: 'Abr',
    ar: 'أبر',
    fr: 'Avr',
    pt: 'Abr',
    de: 'Apr',
    ja: '4月',
    it: 'Apr'
  },
  'Jun': {
    en: 'Jun',
    es: 'Jun',
    ar: 'يون',
    fr: 'Juin',
    pt: 'Jun',
    de: 'Jun',
    ja: '6月',
    it: 'Giu'
  },
  'Jul': {
    en: 'Jul',
    es: 'Jul',
    ar: 'يول',
    fr: 'Juil',
    pt: 'Jul',
    de: 'Jul',
    ja: '7月',
    it: 'Lug'
  },
  'Aug': {
    en: 'Aug',
    es: 'Ago',
    ar: 'أغس',
    fr: 'Août',
    pt: 'Ago',
    de: 'Aug',
    ja: '8月',
    it: 'Ago'
  },
  'Sep': {
    en: 'Sep',
    es: 'Sep',
    ar: 'سبت',
    fr: 'Sep',
    pt: 'Set',
    de: 'Sep',
    ja: '9月',
    it: 'Set'
  },
  'Oct': {
    en: 'Oct',
    es: 'Oct',
    ar: 'أكت',
    fr: 'Oct',
    pt: 'Out',
    de: 'Okt',
    ja: '10月',
    it: 'Ott'
  },
  'Nov': {
    en: 'Nov',
    es: 'Nov',
    ar: 'نوف',
    fr: 'Nov',
    pt: 'Nov',
    de: 'Nov',
    ja: '11月',
    it: 'Nov'
  },
  'Dec': {
    en: 'Dec',
    es: 'Dic',
    ar: 'ديس',
    fr: 'Déc',
    pt: 'Dez',
    de: 'Dez',
    ja: '12月',
    it: 'Dic'
  }
};

@Injectable()
export class LuxonDateAdapter extends DateAdapter<DateTime> {
  constructor(@Optional() @Inject(MAT_DATE_LOCALE) dateLocale: string) {
    super();
    this.setLocale(dateLocale || 'en-US');
  }

  getYear(date: DateTime): number {
    return date.year;
  }

  getMonth(date: DateTime): number {
    // Material expects 0-indexed months
    return date.month - 1;
  }

  getDate(date: DateTime): number {
    return date.day;
  }

  getDayOfWeek(date: DateTime): number {
    // Luxon uses 1-7 (Monday-Sunday), Material expects 0-6 (Sunday-Saturday)
    return date.weekday === 7 ? 0 : date.weekday;
  }

  getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    const formatStr = style === 'long' ? 'long' : style === 'short' ? 'short' : 'narrow';
    return Info.months(formatStr as 'long' | 'short' | 'narrow', { locale: this.locale });
  }

  getDateNames(): string[] {
    return Array.from({length: 31}, (_, i) => String(i + 1));
  }

  getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    const formatStr = style === 'long' ? 'long' : style === 'short' ? 'short' : 'narrow';
    const days = Info.weekdays(formatStr as 'long' | 'short' | 'narrow', { locale: this.locale });
    // Rotate array to start with Sunday
    const sunday = days[6];
    return [sunday, ...days.slice(0, 6)];
  }

  getYearName(date: DateTime): string {
    return date.toFormat('yyyy');
  }

  getFirstDayOfWeek(): number {
    // Return 0 for Sunday (Material's convention)
    return 0;
  }

  getNumDaysInMonth(date: DateTime): number {
    return date.daysInMonth;
  }

  clone(date: DateTime): DateTime {
    return DateTime.fromMillis(date.toMillis());
  }

  createDate(year: number, month: number, date: number): DateTime {
    // month is 0-indexed from Material
    return DateTime.local(year, month + 1, date);
  }

  today(): DateTime {
    return DateTime.local();
  }

  parse(value: any, parseFormat: string): DateTime | null {
    if (value && typeof value === 'string') {
      // Convert moment format to Luxon format
      const luxonFormat = this.convertMomentToLuxonFormat(parseFormat);
      const parsed = DateTime.fromFormat(value, luxonFormat);
      if (parsed.isValid) {
        return parsed;
      }
      // Try ISO format as fallback
      const iso = DateTime.fromISO(value);
      if (iso.isValid) {
        return iso;
      }
    }
    return null;
  }

  format(date: DateTime, displayFormat: string): string {
    const luxonFormat = this.convertMomentToLuxonFormat(displayFormat);
    const formatted = date.toFormat(luxonFormat);
    return this.translateMonthNames(formatted);
  }

  addCalendarYears(date: DateTime, years: number): DateTime {
    return date.plus({ years });
  }

  addCalendarMonths(date: DateTime, months: number): DateTime {
    return date.plus({ months });
  }

  addCalendarDays(date: DateTime, days: number): DateTime {
    return date.plus({ days });
  }

  toIso8601(date: DateTime): string {
    return date.toISO();
  }

  isDateInstance(obj: any): boolean {
    return DateTime.isDateTime(obj);
  }

  isValid(date: DateTime): boolean {
    return date.isValid;
  }

  invalid(): DateTime {
    return DateTime.invalid('Invalid date');
  }

  private convertMomentToLuxonFormat(momentFormat: string): string {
    return momentFormat
      // Year
      .replace(/YYYY/g, 'yyyy')
      .replace(/YY/g, 'yy')
      // Month
      .replace(/MMMM/g, 'LLLL')
      .replace(/MMM/g, 'LLL')
      .replace(/MM/g, 'MM')
      .replace(/M/g, 'M')
      // Day
      .replace(/DD/g, 'dd')
      .replace(/D/g, 'd')
      // Day of week
      .replace(/dddd/g, 'cccc')
      .replace(/ddd/g, 'ccc')
      // AM/PM
      .replace(/A/g, 'a');
  }

  private translateMonthNames(formattedDate: string): string {
    // Get the user's language from localStorage, default to English
    const language = localStorage.getItem('language') || 'en';

    // Replace month names with translated versions
    let translatedDate = formattedDate;
    Object.keys(monthTranslations).forEach(monthName => {
      if (translatedDate.includes(monthName) && monthTranslations[monthName][language]) {
        translatedDate = translatedDate.replace(
          monthName,
          monthTranslations[monthName][language]
        );
      }
    });

    return translatedDate;
  }

  deserialize(value: any): DateTime | null {
    if (typeof value === 'string') {
      return this.parse(value, 'MM/DD/YYYY');
    }
    if (value instanceof Date) {
      return DateTime.fromJSDate(value);
    }
    if (DateTime.isDateTime(value)) {
      return this.clone(value);
    }
    return null;
  }
}

@Injectable()
export class TranslatedLuxonDateAdapter extends LuxonDateAdapter {
  format(date: DateTime, displayFormat: string): string {
    // Get the standard formatted date from the parent class
    const formattedDate = super.format(date, displayFormat);

    // The parent class already handles translation, so just return
    return formattedDate;
  }
}

// Custom date format for MMM d yyyy (e.g., Mar 3 2025)
export const LUXON_DATE_FORMAT = {
  parse: {
    dateInput: 'MM/dd/yyyy', // this is how your date will be parsed from Input
  },
  display: {
    dateInput: 'LLLL d, yyyy', // this is how your date will get displayed on the Input
    monthYearLabel: 'LLL yyyy',
    dateA11yLabel: 'DD',
    monthYearA11yLabel: 'LLLL yyyy'
  }
};
